import axios, { Method } from 'axios';
import qs from 'qs';
import { RootState } from 'Douleutaras/redux/store';
import { COUNTRY_CONFIG } from 'Douleutaras/constants/localization';
import { useLocalization } from 'Douleutaras/hooks/localization';
import { getErrorMessage } from 'Douleutaras/redux/getErrorMessage';
import { CaptureMessage } from 'Douleutaras/providers/CaptureException';

import { MakeRequestParams, MakeRequestParamsConfigure } from './apiTypes';

export const makeRequest = (url, { method, body, includes, additionalHeaders, additionalGetParams, stringifyOptions, configure }:
  MakeRequestParams = {}) => {
  return api => {
    if (!api) {
      throw Error('api passed to makeRequest() is undefined, check that you have correct number of args in createAsyncThunk (should be 2)');
    }

    return apiRequest(url,
      method,
      body,
      includes,
      additionalHeaders,
      additionalGetParams,
      stringifyOptions,
      configure,
      axiosBaseConfiguration)(api);
  };
};

const axiosBaseConfiguration = {
  baseUrl: `${COUNTRY_CONFIG.API_URL[(useLocalization()).countryCode]}`,
  maxRedirects: 0,
  prepareHeaders: (headers, getState: () => RootState | null, withCredentials: boolean) => {
    if (getState !== undefined) {
      const token = (getState() as RootState).auth.token;
      if (withCredentials && token) {
        headers['Authorization'] = `JWT ${token}`;
      }
    }

    return headers;
  }
};

const onApiSuccess = response => {
  return response;
};

const onApiError = async (error, api) => {
  const message = getErrorMessage(error);

  // @Sentry
  CaptureMessage(message);

  return api.rejectWithValue(error);
};

export const apiRequest = (url,
                           method: Method = 'GET',
                           data = {},
                           includes = [],
                           additionalHeaders = {},
                           additionalGetParams = {},
                           stringifyOptions = {},
                           configure: MakeRequestParamsConfigure = {
                             withCredentials: true
                           },
                           { prepareHeaders, baseUrl } = axiosBaseConfiguration) => {

  let extraHeaders = {};
  const v3 = url.indexOf('/v3/') !== -1;
  let parsedQueryParams = url.indexOf('?') === - 1 ? {} : qs.parse(url.split('?')[1]);

  if (includes.length) {
    parsedQueryParams.include = includes.join(',');
  }

  if (Object.keys(additionalGetParams).length > 0) {
    parsedQueryParams = { ...parsedQueryParams, ...additionalGetParams };
  }

  if (!v3) {
    parsedQueryParams['format'] = 'json';
  }

  const stringifiedQueryParams = qs.stringify(parsedQueryParams, stringifyOptions);
  const urlAppendix = stringifiedQueryParams ? `?${stringifiedQueryParams}` : '';

  if (Object.keys(additionalHeaders).length > 0) {
    extraHeaders = { ...extraHeaders, ...additionalHeaders };
  }

  // @TODO change this workaround. Legacy and new API accept different types of content-type
  extraHeaders['Content-Type'] = v3 ? 'application/vnd.api+json' : 'application/json';

  const readyUrl = `${baseUrl}${url.split('?')[0]}${urlAppendix}`;

  return api => {
    return axios({
      headers: prepareHeaders(extraHeaders, api ? api.getState : undefined, configure.withCredentials),
      // Add trailing slash to url if not exists
      url: readyUrl.replace(/\/?(\?|#|$)/, '/$1'),
      method,
      data,
      withCredentials: configure.withCredentials
    })
      .then(onApiSuccess)
      .catch(err => onApiError(err, api));
  };
};
